import { Locale, TranslationKeys } from "../locales";

/**
 * 프론트엔드 Locale을 백엔드 lang 파라미터로 변환
 * @param locale 프론트엔드 Locale (ko, en, zh, ja, vi, es)
 * @returns 백엔드 lang 파라미터 (kor, eng, zho, jpn, vie, spa)
 */
export const convertLocaleToLang = (locale: Locale): string => {
  const langMap: Record<Locale, string> = {
    ko: "kor",
    en: "eng",
    zh: "zho",
    ja: "jpn",
    vi: "vie",
    es: "spa",
  };
  return langMap[locale] || "kor";
};

/**
 * 백엔드 event_type (다국어 가능)을 언어팩 키로 변환
 * @param eventType 백엔드에서 받은 event_type 값 (한국어, 영어, 일본어, 중국어, 베트남어, 스페인어 가능)
 * @param t 언어팩 객체 (현재 언어의 번역값들을 포함)
 * @returns 언어팩 키 (vehicleRolloverEvent, areaExitEvent, etc.)
 */
export const getEventTypeKey = (
  eventType: string,
  t: TranslationKeys
): keyof TranslationKeys["control"] => {
  // 이벤트명에서 전압값 등 추가 정보 제거 (예: "배터리부족 (0.00V)" -> "배터리부족")
  const cleanEventType = eventType
    .replace(/\s*\([^)]*\)/g, "") // 괄호와 내용 제거 (예: " (0.00V)")
    .replace(/\s*\[[^\]]*\]/g, "") // 대괄호와 내용 제거
    .trim();
  
  // 모든 언어의 백엔드 값을 매핑
  const eventTypeMap: Record<string, keyof TranslationKeys["control"]> = {
    // 한국어
    "배터리부족": "batteryLowEvent",
    "사용지역이탈": "areaExitEvent",
    "위험운전": "dangerousDrivingEvent",
    "차량전복": "vehicleRolloverEvent",
    "시동방치": "engineAbandonmentEvent",
    "위험지역진입": "dangerousAreaEntryEvent",
    "도난감지": "theftDetection",
    // 영어
    "Low Battery": "batteryLowEvent",
    "Area Exit": "areaExitEvent",
    "Dangerous Driving": "dangerousDrivingEvent",
    "Vehicle Rollover": "vehicleRolloverEvent",
    "Engine Abandonment": "engineAbandonmentEvent",
    "Dangerous Area Entry": "dangerousAreaEntryEvent",
    "Theft Detection": "theftDetection",
    // 일본어 (예상 값)
    "バッテリー不足": "batteryLowEvent",
    "使用地域離脱": "areaExitEvent",
    "危険運転": "dangerousDrivingEvent",
    "車両転倒": "vehicleRolloverEvent",
    "始動放置": "engineAbandonmentEvent",
    "危険区域進入": "dangerousAreaEntryEvent",
    "盗難検知": "theftDetection",
    // 중국어 (예상 값)
    "电池不足": "batteryLowEvent",
    "使用区域脱离": "areaExitEvent",
    "危险驾驶": "dangerousDrivingEvent",
    "车辆翻倒": "vehicleRolloverEvent",
    "启动放置": "engineAbandonmentEvent",
    "危险区域进入": "dangerousAreaEntryEvent",
    "盗窃检测": "theftDetection",
    // 베트남어 (예상 값)
    "Pin yếu": "batteryLowEvent",
    "Rời khu vực sử dụng": "areaExitEvent",
    "Lái xe nguy hiểm": "dangerousDrivingEvent",
    "Lật xe": "vehicleRolloverEvent",
    "Bỏ quên khởi động": "engineAbandonmentEvent",
    "Vào khu vực nguy hiểm": "dangerousAreaEntryEvent",
    "Phát hiện trộm cắp": "theftDetection",
    // 스페인어 (예상 값)
    "Batería baja": "batteryLowEvent",
    "Salida del área": "areaExitEvent",
    "Conducción peligrosa": "dangerousDrivingEvent",
    "Vuelco de vehículo": "vehicleRolloverEvent",
    "Abandono del motor": "engineAbandonmentEvent",
    "Entrada a zona peligrosa": "dangerousAreaEntryEvent",
    "Detección de robo": "theftDetection",
  };

  // 직접 매칭되는 경우 (원본과 정리된 버전 모두 확인)
  if (eventTypeMap[eventType]) {
    return eventTypeMap[eventType];
  }
  if (eventTypeMap[cleanEventType]) {
    return eventTypeMap[cleanEventType];
  }

  // 언어팩 값과 비교하여 매칭 (대소문자 무시, 공백 제거)
  const eventKeys: (keyof TranslationKeys["control"])[] = [
    "batteryLowEvent",
    "areaExitEvent",
    "dangerousDrivingEvent",
    "vehicleRolloverEvent",
    "engineAbandonmentEvent",
    "dangerousAreaEntryEvent",
    "theftDetection",
  ];

  // 정규화된 비교 함수
  const normalize = (str: string) => str.trim().toLowerCase().replace(/\s+/g, " ");

  for (const key of eventKeys) {
    const translatedValue = t.control[key];
    if (typeof translatedValue === "string") {
      // 정확한 매칭 (원본과 정리된 버전 모두 확인)
      if (translatedValue === eventType || translatedValue === cleanEventType) {
        return key;
      }
      // 정규화된 매칭 (대소문자, 공백 무시)
      if (normalize(translatedValue) === normalize(eventType) || 
          normalize(translatedValue) === normalize(cleanEventType)) {
        return key;
      }
      // 부분 매칭 (배터리부족 같은 경우 전압값이 붙어있을 수 있음)
      if (eventType.includes(translatedValue) || cleanEventType.includes(translatedValue)) {
        return key;
      }
      if (translatedValue.includes(cleanEventType) || translatedValue.includes(eventType)) {
        return key;
      }
    }
  }

  // 매칭되지 않으면 기본값 반환
  return "normal";
};

/**
 * 백엔드 mobility_type (다국어 가능)을 언어팩 값으로 변환
 * @param mobilityType 백엔드에서 받은 mobility_type 값
 * @param t 언어팩 객체 (현재 언어의 번역값들을 포함)
 * @returns 언어팩 값 (트랙터, 스피드스프레이어, etc.)
 */
export const getMobilityTypeText = (
  mobilityType: string,
  t: TranslationKeys
): string => {
  // 한국어 기기종류와 언어팩 키 매핑
  const mobilityTypeMap: Record<string, keyof TranslationKeys["control"]> = {
    "트랙터": "tractor",
    "스피드스프레이어": "speedSprayer",
    "사륜오토바이": "fourWheelMotorcycle",
    "이륜오토바이": "twoWheelMotorcycle",
    "1톤 트럭": "oneTonTruck",
    "보행관리기": "walkingManagement",
    "승용관리기": "ridingManagement",
    "농업용고소작업차": "agriculturalHighWorkVehicle",
    "농업용운반차": "agriculturalTransportVehicle",
    "농업용로더": "agriculturalLoader",
    "승용예초기": "ridingWeedCutter",
    "승용제초기": "ridingWeedRemover",
    "승용이앙기": "ridingTransplanter",
    "퇴비살포기": "fertilizerSpreader",
    "농약살포기": "pesticideSpreader",
    "콤바인": "combine",
    "굴삭기": "excavator",
    "경운기": "cultivator",
    "잔가지파쇄기": "branchCrusher",
    "기타": "etc",
  };

  // 직접 매칭되는 경우
  if (mobilityTypeMap[mobilityType]) {
    const key = mobilityTypeMap[mobilityType];
    const translatedValue = t.control[key];
    return typeof translatedValue === "string" ? translatedValue : mobilityType;
  }

  // 언어팩 값과 비교하여 매칭
  const mobilityKeys: (keyof TranslationKeys["control"])[] = [
    "tractor",
    "speedSprayer",
    "fourWheelMotorcycle",
    "twoWheelMotorcycle",
    "oneTonTruck",
    "walkingManagement",
    "ridingManagement",
    "agriculturalHighWorkVehicle",
    "agriculturalTransportVehicle",
    "agriculturalLoader",
    "ridingWeedCutter",
    "ridingWeedRemover",
    "ridingTransplanter",
    "fertilizerSpreader",
    "pesticideSpreader",
    "combine",
    "excavator",
    "cultivator",
    "branchCrusher",
    "etc",
  ];

  // 정규화된 비교 함수
  const normalize = (str: string) => str.trim().toLowerCase().replace(/\s+/g, " ");

  for (const key of mobilityKeys) {
    const translatedValue = t.control[key];
    if (typeof translatedValue === "string") {
      // 정확한 매칭
      if (translatedValue === mobilityType) {
        return translatedValue;
      }
      // 정규화된 매칭 (대소문자, 공백 무시)
      if (normalize(translatedValue) === normalize(mobilityType)) {
        return translatedValue;
      }
    }
  }

  // 매칭되지 않으면 원본 값 반환
  return mobilityType;
};

/**
 * 이벤트 타입 키를 언어팩 값으로 변환
 * @param eventKey 언어팩 키 (vehicleRolloverEvent, areaExitEvent, etc.)
 * @param t 언어팩 객체 (현재 언어의 번역값들을 포함)
 * @returns 언어팩 값 (차량전복, Vehicle Rollover, etc.)
 */
export const getEventTypeValue = (
  eventKey: string,
  t: TranslationKeys
): string => {
  const eventKeys: (keyof TranslationKeys["control"])[] = [
    "batteryLowEvent",
    "areaExitEvent",
    "dangerousDrivingEvent",
    "vehicleRolloverEvent",
    "engineAbandonmentEvent",
    "dangerousAreaEntryEvent",
    "theftDetection",
  ];

  if (eventKeys.includes(eventKey as keyof TranslationKeys["control"])) {
    const translatedValue = t.control[eventKey as keyof TranslationKeys["control"]];
    if (typeof translatedValue === "string") {
      return translatedValue;
    }
  }

  // 매칭되지 않으면 원본 키 반환
  return eventKey;
};

